import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchCoupons, getSponsorTiers, startSponsorCheckout, getSponsorStatus, getDotdBooked, startDotdCheckout } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, Star, Sparkles, TrendingUp, Zap, CheckCircle2, Crown, Calendar } from "lucide-react";
import StoreLogo from "@/components/StoreLogo";

const TIER_ICONS = { bronze: Star, silver: Sparkles, gold: Zap };
const TIER_COLORS = {
  bronze: { bg: "#f7c873", ring: "#f2a93b" },
  silver: { bg: "#e5e7eb", ring: "#9ca3af" },
  gold: { bg: "#fde68a", ring: "#eab308" },
};

export default function Sponsor() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tiers, setTiers] = useState({});
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tier, setTier] = useState("silver");
  const [days, setDays] = useState(14);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    getSponsorTiers().then((d) => setTiers(d.tiers || {})).catch(() => {});
  }, []);

  useEffect(() => {
    const checkout = searchParams.get("checkout");
    const dotd = searchParams.get("dotd");
    const sid = searchParams.get("session_id");
    if (checkout === "success" && sid) {
      toast.success("Payment received — your deal will be pinned within a minute 🎉");
      getSponsorStatus(sid).catch(() => {});
      const sp = new URLSearchParams(searchParams);
      sp.delete("checkout");
      sp.delete("session_id");
      setSearchParams(sp);
    } else if (checkout === "cancel") {
      toast("Checkout cancelled");
      const sp = new URLSearchParams(searchParams);
      sp.delete("checkout");
      setSearchParams(sp);
    } else if (dotd === "success" && sid) {
      toast.success("Deal of the Day slot locked in 🌟 We'll spotlight your deal on the chosen date(s).");
      getSponsorStatus(sid).catch(() => {});
      const sp = new URLSearchParams(searchParams);
      sp.delete("dotd");
      sp.delete("session_id");
      setSearchParams(sp);
    } else if (dotd === "cancel") {
      toast("DOTD checkout cancelled");
      const sp = new URLSearchParams(searchParams);
      sp.delete("dotd");
      setSearchParams(sp);
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!q) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const data = await fetchCoupons({ q, limit: 12 });
        setResults(data);
      } catch {
        setResults([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const priceCents = tiers[tier]?.price_per_day_cents || 0;
  const totalCents = priceCents * days;

  const startCheckout = async () => {
    if (!selected) return;
    setStarting(true);
    try {
      const r = await startSponsorCheckout({
        coupon_id: selected.id,
        tier,
        days,
        origin_url: window.location.origin,
      });
      window.location.href = r.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10" data-testid="sponsor-page">
      {/* Hero */}
      <section className="mb-10">
        <div className="text-[10px] tracking-[0.35em] text-[#5c5c5c] mb-2">
          FOR RETAILERS · SELF-SERVE
        </div>
        <h1 className="serif text-5xl md:text-7xl leading-[0.95] tracking-tight">
          Put your deal <span className="italic text-[#ff6b6b]">at the top.</span>
        </h1>
        <p className="mt-4 text-lg text-[#3a3a38] max-w-2xl leading-relaxed">
          Every shopper on The Couponer sees the top of the feed first. Pin your
          coupon there in under 60 seconds — no salesperson, no contract, no
          minimums. Pay with a card, we auto-pin.
        </p>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* LEFT: Search + select */}
        <div className="lg:col-span-3">
          <h2 className="serif text-3xl mb-3">1 · Find your deal</h2>
          <div className="relative mb-4">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5c5c5c]" />
            <Input
              data-testid="sponsor-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search for your store — e.g. Nike, Hey Dude, Amazon..."
              className="pl-11 h-12 rounded-full border-2 border-[#2d2a32] bg-white shadow-teal"
            />
          </div>

          <div className="space-y-2 max-h-[520px] overflow-y-auto pr-2">
            {results.map((c) => {
              const isSel = selected?.id === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setSelected(c)}
                  data-testid={`sponsor-result-${c.id}`}
                  className={`w-full text-left border-2 rounded-xl p-3 bg-white flex items-center gap-3 transition-transform ${
                    isSel
                      ? "border-[#ff6b6b] shadow-coral -translate-y-0.5"
                      : "border-[#2d2a32]/30 hover:border-[#2d2a32]"
                  }`}
                >
                  <StoreLogo store={c.store} storeUrl={c.store_url} size={40} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] tracking-[0.2em] text-[#5c5c5c]">
                      {c.discount_label} · {c.category?.toUpperCase()}
                    </div>
                    <div className="serif text-lg truncate">{c.store}</div>
                    <div className="text-xs text-[#3a3a38] truncate">{c.title}</div>
                  </div>
                  {isSel && <CheckCircle2 className="w-5 h-5 text-[#ff6b6b] shrink-0" />}
                </button>
              );
            })}
            {!q && (
              <p className="text-sm text-[#5c5c5c] italic px-2">
                Type your store name above. Don&#39;t see it? Submit a coupon first via the SUBMIT page.
              </p>
            )}
            {q && results.length === 0 && (
              <p className="text-sm text-[#5c5c5c] italic px-2">No deals match "{q}".</p>
            )}
          </div>
        </div>

        {/* RIGHT: Tier + duration + checkout */}
        <div className="lg:col-span-2">
          <div className="sticky top-24 space-y-6">
            <div>
              <h2 className="serif text-3xl mb-3">2 · Pick a tier</h2>
              <div className="space-y-2">
                {["bronze", "silver", "gold"].map((k) => {
                  const t = tiers[k];
                  if (!t) return null;
                  const Icon = TIER_ICONS[k];
                  const colors = TIER_COLORS[k];
                  const active = tier === k;
                  return (
                    <button
                      key={k}
                      onClick={() => setTier(k)}
                      data-testid={`tier-${k}`}
                      className={`w-full text-left border-2 rounded-xl p-3 flex items-center gap-3 transition-transform ${
                        active
                          ? "border-[#2d2a32] shadow-mustard -translate-y-0.5"
                          : "border-[#2d2a32]/30 hover:border-[#2d2a32] bg-white"
                      }`}
                      style={active ? { background: colors.bg } : {}}
                    >
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center border-2 border-[#2d2a32] shrink-0"
                        style={{ background: colors.bg }}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="serif text-lg leading-none">{t.label}</div>
                        <div className="text-[11px] text-[#5c5c5c] mt-0.5 leading-snug">
                          {t.description}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="mono text-lg font-bold">
                          ${(t.price_per_day_cents / 100).toFixed(0)}
                        </div>
                        <div className="text-[9px] tracking-widest text-[#5c5c5c]">/DAY</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <h2 className="serif text-3xl mb-3">3 · How many days?</h2>
              <div className="flex gap-2 flex-wrap">
                {[7, 14, 30, 60].map((n) => (
                  <button
                    key={n}
                    onClick={() => setDays(n)}
                    data-testid={`days-${n}`}
                    className={`px-4 py-2 border-2 rounded-full text-sm font-bold ${
                      days === n
                        ? "border-[#2d2a32] bg-[#2d2a32] text-white"
                        : "border-[#2d2a32]/30 hover:border-[#2d2a32] bg-white"
                    }`}
                  >
                    {n} days
                  </button>
                ))}
              </div>
            </div>

            {/* Summary + Checkout */}
            <div className="border-2 border-[#2d2a32] rounded-xl p-5 bg-white hard-shadow">
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-[10px] tracking-[0.25em] text-[#5c5c5c]">TOTAL</span>
                <span
                  className="serif text-4xl leading-none"
                  data-testid="sponsor-total"
                >
                  ${(totalCents / 100).toFixed(2)}
                </span>
              </div>
              <div className="text-xs text-[#5c5c5c] mb-4">
                {tiers[tier]?.label} · {days} days · one-time payment
              </div>
              <button
                onClick={startCheckout}
                disabled={!selected || starting}
                data-testid="sponsor-checkout-btn"
                className="w-full h-12 rounded-full bg-[#ff6b6b] hover:bg-[#e85555] text-white font-bold tracking-[0.2em] text-sm hard-shadow disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {!selected
                  ? "SELECT A DEAL TO CONTINUE"
                  : starting
                  ? "OPENING CHECKOUT..."
                  : `PIN & PAY $${(totalCents / 100).toFixed(2)}`}
              </button>
              <p className="text-[10px] text-[#5c5c5c] mt-3 text-center leading-relaxed">
                Secure checkout via Stripe. Your deal is pinned automatically after payment.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Trust strip */}
      <section className="mt-16 pt-8 border-t-2 border-dashed border-[#2d2a32]">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <TrustCard
            icon={TrendingUp}
            title="Real CTR data"
            body="See exactly how many shoppers clicked your pinned deal. Every click is logged."
          />
          <TrustCard
            icon={Zap}
            title="Instant activation"
            body="Payment → pin in under 60 seconds. No approval, no back-and-forth."
          />
          <TrustCard
            icon={CheckCircle2}
            title="No commitment"
            body="Pin for a week, a month, or a year. Prices are flat per day. Cancel by not renewing."
          />
        </div>
      </section>

      {/* Sponsored Deal of the Day — premium slot */}
      <DotdSlotSection selected={selected} />
    </div>
  );
}

function DotdSlotSection({ selected }) {
  const [booked, setBooked] = useState([]);
  const [pricePerDayCents, setPricePerDayCents] = useState(9900);
  const [dates, setDates] = useState([]); // ISO strings
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    getDotdBooked()
      .then((r) => {
        setBooked((r.booked || []).map((b) => b.date));
        if (r.price_per_day_cents) setPricePerDayCents(r.price_per_day_cents);
      })
      .catch(() => {});
  }, []);

  // Show a 21-day picker starting today (3 weeks). We derive ISO dates from UTC
  // to match backend validation (which uses UTC "today"). This avoids a
  // late-night Pacific-time edge case where the local "today" is still visible
  // but backend UTC has already rolled forward → rejects as "past".
  const days = [];
  const nowUtc = new Date();
  const todayUtc = new Date(Date.UTC(
    nowUtc.getUTCFullYear(),
    nowUtc.getUTCMonth(),
    nowUtc.getUTCDate(),
  ));
  for (let i = 0; i < 21; i++) {
    const d = new Date(todayUtc);
    d.setUTCDate(todayUtc.getUTCDate() + i);
    const iso = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
    days.push({ iso, d });
  }

  const toggleDate = (iso) => {
    if (booked.includes(iso)) return;
    setDates((prev) =>
      prev.includes(iso) ? prev.filter((x) => x !== iso) : [...prev, iso]
    );
  };

  const totalCents = pricePerDayCents * dates.length;

  const startDotd = async () => {
    if (!selected) {
      toast.error("Pick a deal above first (Step 1)");
      return;
    }
    if (dates.length === 0) {
      toast.error("Pick at least one date");
      return;
    }
    setStarting(true);
    try {
      const r = await startDotdCheckout({
        coupon_id: selected.id,
        dates,
        origin_url: window.location.origin,
      });
      window.location.href = r.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "DOTD checkout failed");
    } finally {
      setStarting(false);
    }
  };

  return (
    <section
      className="mt-16 border-2 border-[#2d2a32] rounded-2xl p-6 md:p-8 hard-shadow relative overflow-hidden"
      style={{ background: "linear-gradient(135deg,#fff2d6 0%,#fde68a 100%)" }}
      data-testid="dotd-slot-section"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-start mb-6">
        <div>
          <div className="text-[10px] tracking-[0.35em] text-[#5c5c5c] mb-1 flex items-center gap-2">
            <Crown className="w-3 h-3" /> PREMIUM · TOP-OF-HOME SPOTLIGHT
          </div>
          <h2 className="serif text-4xl md:text-5xl leading-[0.98] tracking-tight">
            Own the <span className="italic text-[#ff6b6b]">Deal of the Day</span>.
          </h2>
          <p className="mt-3 text-[15px] text-[#3a3a38] leading-relaxed max-w-2xl">
            Lock the hero card at the top of the homepage for a whole day. One retailer per date — first-come, first-served. Pick your dates below.
          </p>
        </div>
        <div className="border-2 border-[#2d2a32] rounded-xl px-5 py-4 bg-white text-center shrink-0 hard-shadow-sm">
          <div className="serif text-4xl leading-none">
            ${(pricePerDayCents / 100).toFixed(0)}
          </div>
          <div className="text-[10px] tracking-[0.25em] text-[#5c5c5c] mt-1">/DAY</div>
        </div>
      </div>

      <div className="mb-4">
        <div className="text-[10px] tracking-[0.3em] text-[#5c5c5c] mb-2 flex items-center gap-2">
          <Calendar className="w-3 h-3" /> PICK YOUR DATES · NEXT 3 WEEKS
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 gap-2" data-testid="dotd-date-picker">
          {days.map(({ iso, d }) => {
            const isBooked = booked.includes(iso);
            const isPicked = dates.includes(iso);
            const label = d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
            const dow = d.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" });
            return (
              <button
                key={iso}
                onClick={() => toggleDate(iso)}
                disabled={isBooked}
                data-testid={`dotd-day-${iso}`}
                className={`h-16 rounded-lg border-2 flex flex-col items-center justify-center text-xs font-bold transition-all ${
                  isBooked
                    ? "border-[#5c5c5c]/30 bg-[#5c5c5c]/10 text-[#5c5c5c] cursor-not-allowed line-through"
                    : isPicked
                    ? "border-[#2d2a32] bg-[#ff6b6b] text-white -translate-y-0.5 shadow-coral"
                    : "border-[#2d2a32]/40 bg-white hover:border-[#2d2a32] hover:-translate-y-0.5"
                }`}
              >
                <span className="text-[9px] tracking-widest opacity-70">{dow.toUpperCase()}</span>
                <span className="text-[13px] leading-tight">{label}</span>
                {isBooked && <span className="text-[8px] tracking-widest">BOOKED</span>}
              </button>
            );
          })}
        </div>
      </div>

      <div className="border-2 border-[#2d2a32] rounded-xl p-5 bg-white flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.25em] text-[#5c5c5c]">
            {dates.length === 0
              ? "NO DATES SELECTED"
              : `${dates.length} DATE${dates.length !== 1 ? "S" : ""} SELECTED`}
          </div>
          <div className="serif text-4xl leading-none mt-1" data-testid="dotd-total">
            ${(totalCents / 100).toFixed(2)}
          </div>
          {!selected && (
            <div className="text-[11px] text-[#ff6b6b] mt-1 font-bold">
              ↑ Pick a deal in Step 1 first
            </div>
          )}
          {selected && (
            <div className="text-[11px] text-[#5c5c5c] mt-1">
              For <strong>{selected.store}</strong> · {selected.discount_label}
            </div>
          )}
        </div>
        <button
          onClick={startDotd}
          disabled={!selected || dates.length === 0 || starting}
          data-testid="dotd-checkout-btn"
          className="h-12 px-6 rounded-full bg-[#2d2a32] hover:bg-[#ff6b6b] text-white font-bold tracking-[0.2em] text-sm hard-shadow disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {starting
            ? "OPENING CHECKOUT..."
            : dates.length === 0
            ? "PICK DATES ABOVE"
            : `LOCK DOTD · $${(totalCents / 100).toFixed(0)}`}
        </button>
      </div>
    </section>
  );
}

function TrustCard({ icon: Icon, title, body }) {
  return (
    <div className="border-2 border-[#2d2a32] rounded-xl p-5 bg-white">
      <div className="w-10 h-10 rounded-full border-2 border-[#2d2a32] bg-[#4ecdc4] flex items-center justify-center mb-3">
        <Icon className="w-5 h-5" />
      </div>
      <div className="serif text-xl mb-1">{title}</div>
      <div className="text-sm text-[#5c5c5c] leading-relaxed">{body}</div>
    </div>
  );
}
